import React, { useMemo, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import PageHeader from '../core/PageHeader';
import { useMarkAllNotificationsRead, useMarkNotificationRead, useNotifications } from '../hooks/notifications';

const NotificationsPage: React.FC = () => {
    const navigate = useNavigate();
    const [search, setSearch] = useState('');
    const [showUnreadOnly, setShowUnreadOnly] = useState(false);
    const { data, isLoading } = useNotifications({ limit: 150, unreadOnly: showUnreadOnly });
    const markReadMutation = useMarkNotificationRead();
    const markAllReadMutation = useMarkAllNotificationsRead();

    const filteredNotifications = useMemo(() => {
        const term = search.trim().toLowerCase();
        return data.items.filter((notification) => {
            if (!term) {
                return true;
            }
            return [notification.title, notification.message, notification.actor?.displayName ?? '']
                .join(' ')
                .toLowerCase()
                .includes(term);
        });
    }, [data.items, search]);

    const openNotification = async (notificationId: number, targetUrl: string) => {
        await markReadMutation.mutateAsync({ id: notificationId });
        if (targetUrl) {
            navigate(targetUrl);
        }
    };

    return (
        <Box sx={{ py: { xs: 3, md: 4 }, width: '100%', maxWidth: '100%' }}>
            <Container maxWidth="xl">
                <PageHeader
                    title="Benachrichtigungen"
                    subtitle="Alle Mitteilungen aus dem Verbindungsleben."
                    action={(
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => { void markAllReadMutation.mutateAsync(); }}
                            disabled={data.unread_count === 0 || markAllReadMutation.isPending}
                        >
                            Alle als gelesen markieren
                        </Button>
                    )}
                />

                <Stack spacing={2.5}>
                    <Card>
                        <CardContent>
                            <Stack spacing={1.5}>
                                <TextField
                                    label="Benachrichtigungen durchsuchen"
                                    value={search}
                                    onChange={(event) => setSearch(event.target.value)}
                                    size="small"
                                />
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                    <Chip
                                        label="Alle"
                                        size="small"
                                        color={!showUnreadOnly ? 'primary' : 'default'}
                                        onClick={() => setShowUnreadOnly(false)}
                                    />
                                    <Chip
                                        label="Nur ungelesen"
                                        size="small"
                                        color={showUnreadOnly ? 'primary' : 'default'}
                                        onClick={() => setShowUnreadOnly(true)}
                                    />
                                </Stack>
                            </Stack>
                        </CardContent>
                    </Card>

                    {isLoading && (
                        <Typography color="text.secondary">Lade Benachrichtigungen…</Typography>
                    )}

                    {!isLoading && filteredNotifications.length === 0 && (
                        <Card>
                            <CardContent>
                                <Typography color="text.secondary">
                                    Keine Benachrichtigungen gefunden.
                                </Typography>
                            </CardContent>
                        </Card>
                    )}

                    <Stack spacing={1.5}>
                        {filteredNotifications.map((notification) => (
                            <Card
                                key={notification.id}
                                sx={{
                                    borderColor: notification.is_read ? 'divider' : 'primary.light',
                                    backgroundColor: notification.is_read ? 'background.paper' : '#f7fbf4',
                                    cursor: notification.target_url ? 'pointer' : 'default',
                                }}
                                onClick={() => {
                                    void openNotification(notification.id, notification.target_url);
                                }}
                            >
                                <CardContent>
                                    <Stack spacing={0.75}>
                                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                                            {!notification.is_read && <Chip size="small" color="primary" label="Neu" />}
                                            <Typography variant="caption" color="text.secondary">
                                                {new Date(notification.created_at).toLocaleString('de-DE')}
                                            </Typography>
                                        </Stack>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                            {notification.title}
                                        </Typography>
                                        {notification.message && (
                                            <Typography variant="body2" color="text.secondary">
                                                {notification.message}
                                            </Typography>
                                        )}
                                        {notification.actor?.displayName && (
                                            <Typography variant="caption" color="text.secondary">
                                                von {notification.actor.displayName}
                                            </Typography>
                                        )}
                                    </Stack>
                                </CardContent>
                            </Card>
                        ))}
                    </Stack>
                </Stack>
            </Container>
        </Box>
    );
};

export default NotificationsPage;
